import React, { useEffect } from "react";

// Material UI
import Button from "components/CustomButtons/Button";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { Edit } from "@material-ui/icons";
import {
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";

// Custom Components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardTitle from "components/Card/CardTitle";
import CardFooter from "components/Card/CardFooter.js";

import { createProjectAPI } from "views/APIs/ProjectsUtility";
import { addPlanToProject } from "views/APIs/SolutionGeneratorUtility";
import { AllModalLauncher } from "./AllModalLauncher";

const useStyles = makeStyles((theme) => ({
  phantomCardHeader: {
    backgroundColor: "rgba(255,255,255,0.08)",
    border: "1px dashed rgba(255,255,255,0.75)",
    borderRadius: 12,
  },
  phantomCard: {
    border: "1px dashed rgba(255,255,255,0.75)",
    background: "rgba(255,255,255,0.08)",
    maxWidth: "50%",
    borderRadius: 12,
    marginBottom: 12,
  },
  stepLabelText: {
    fontWeight: "bold",
  },
  stepDescription: {
    color: "#666",
  },
}));

export default function PhantomCard({
  title,
  description,
  loader,
  toolboxType,
  setTitleDescriptionForStep,
  plan,
  isLocked,
}) {
  const CARD_STATUS = "Not Created Yet";
  const styles = useStyles();
  const [openModal, setOpenModal] = React.useState(false);

  const [dotsCount, setDotsCount] = React.useState(0);
  const images = require.context("assets/img/create_cards_16_9", true);

  useEffect(() => {
    if (loader) {
      const interval = setInterval(() => {
        setDotsCount((prev) => (prev === 3 ? 0 : prev + 1));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [loader]);

  const handleProjectCreationandPlanAddition = async (title, description) => {
    const formData = new FormData();
    formData.append("project_name", title);
    formData.append("project_description", description);
    const response = await createProjectAPI(formData);

    const formDataforPlan = new FormData();
    formDataforPlan.append("project_id", response._id);
    plan["plan"]["steps"][0]._id = response._id;
    formDataforPlan.append("plan", JSON.stringify(plan));
    await addPlanToProject(formDataforPlan);
    // open the project in a new tab
    window.open(`/create/project-design/projects/${response._id}`, "_blank");
    window.location.reload();
    return response;
  };

  const createToolboxComponent = async () => {
    let response;
    const formData = new FormData();
    switch (toolboxType) {
      case "PROJECT":
        response = await handleProjectCreationandPlanAddition(
          title,
          description
        );
        break;
    }
    setOpenModal(false);
    setTitleDescriptionForStep(title, description, response._id);
  };

  return (
    <Card
      elevation={0}
      style={{
        marginTop: 40,
        border: "1px dashed rgba(255,255,255,0.75)",
        background: "rgba(255,255,255,0.08)",
        borderRadius: 12,
        color: "#EAF6F9",
        maxWidth: "40%",
      }}
    >
      <CardHeader icon text>
        <CardTitle className={styles.phantomCardHeader} color="quCreateColor">
          {title}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <Typography
          variant="body2"
          style={{
            color: "rgba(234,246,249,0.9)",
            minHeight: "100px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          {description}
        </Typography>
      </CardContent>
      <CardFooter stats style={{ marginTop: 0, paddingBottom: 10 }}>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Status (left) */}
          <Tooltip title={CARD_STATUS} arrow>
            <Typography
              variant="body2"
              style={{
                color: "#EAF6F9",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "calc(100% - 120px)",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Edit /> {CARD_STATUS}
            </Typography>
          </Tooltip>

          {/* Create (right) */}
          <Button
            variant="contained"
            style={{ background: "#EAF6F9", color: "#20677b" }}
            onClick={() => setOpenModal(true)}
            disabled={isLocked}
          >
            {loader ? <>Creating{".".repeat(dotsCount)}</> : "Create"}
          </Button>
        </div>
      </CardFooter>
      {/* create a modal taking in the name and description for the toolboxtype prefilled with the title and description */}

      {toolboxType === "PROJECT" && (
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="form-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="form-dialog-title">
            Create a{" "}
            {toolboxType.replace("_", " ").charAt(0).toUpperCase() +
              toolboxType.replace("_", " ").slice(1).toLowerCase()}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Name"
              type="text"
              fullWidth
              required
              value={title}
              onChange={(event) =>
                setTitleDescriptionForStep(
                  event.target.value,
                  description,
                  "none"
                )
              }
            />
            <TextField
              margin="dense"
              id="brief"
              label="Brief Description"
              type="text"
              fullWidth
              multiline
              required
              rows={3}
              value={description}
              onChange={(event) =>
                setTitleDescriptionForStep(title, event.target.value, "none")
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)} color="quCreateColor">
              Cancel
            </Button>
            <Button
              onClick={createToolboxComponent}
              color="quCreateColor"
              disabled={loader || !title || !description}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {toolboxType !== "PROJECT" && (
        <AllModalLauncher
          projectId={plan.plan.steps[0]._id || ""}
          isCourse={false}
          toolboxType={toolboxType}
          name={title}
          description={description}
          setTitleDescriptionForStep={setTitleDescriptionForStep}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />
      )}
    </Card>
  );
}
